import React from 'react';
import { ActivityIndicator, FlatList, StyleSheet, View } from 'react-native';
import { useSafeAreaFrame } from 'react-native-safe-area-context';
import { mvs } from 'react-native-size-matters';

import { PodcastTitleCard } from './PodcastTitleCard';
import { COLORS, DataState } from '../../constants';
import { getResponsiveCardWidth } from '../../helpers/getResponsiveCardWidth';
import { useDeferredMount } from '../../hooks/useDeferredMount';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { PodcastModel } from '../../models';

export interface AllPodcastGridProps {
  podcastList: Partial<PodcastModel>[] | null;
  podcastListState: DataState;
  onPodcastPress: (podcastUrl: string) => void;
}

export const AllPodcastGrid: React.FC<AllPodcastGridProps> = ({
  podcastList,
  podcastListState,
  onPodcastPress,
}) => {
  const { bodyHorizontalPadding } = useResponsiveLayout();
  const { width } = useSafeAreaFrame();
  const cardWidth = getResponsiveCardWidth({
    width: width,
    numColumns: 2,
    horizontalPadding: bodyHorizontalPadding,
  });

  const renderPodcast = ({ item }: { item: Partial<PodcastModel> }) => (
    <View>
      <PodcastTitleCard
        podcastThumbnail={item.logo}
        podcastName={item.title || ''}
        onPress={() => onPodcastPress(item.url || '')}
        customWidth={cardWidth}
      />
    </View>
  );

  // deferred mount to avoid intermittent zero-height race inside modal
  const mounted = useDeferredMount();

  if (podcastListState === DataState.LOADING) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color={COLORS.WHITE} />
      </View>
    );
  }

  if (!mounted) {
    return <View style={{ flex: 1 }} />;
  }

  return (
    <FlatList
      data={podcastList || []}
      renderItem={renderPodcast}
      keyExtractor={item => item.id || ''}
      numColumns={2}
      key={'2-cols'}
      showsVerticalScrollIndicator={false}
      nestedScrollEnabled
      removeClippedSubviews={true}
      style={{ flex: 1 }}
      contentContainerStyle={[
        styles.listContainer,
        { paddingHorizontal: bodyHorizontalPadding },
      ]}
      columnWrapperStyle={styles.row}
      onLayout={() => {}}
      maxToRenderPerBatch={4}
      initialNumToRender={6}
      windowSize={5}
    />
  );
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  listContainer: {
    paddingBottom: mvs(32),
  },
  row: {
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginBottom: mvs(12),
    gap: mvs(12),
  },
});

export default AllPodcastGrid;
